import { Directive, ElementRef, input, AfterViewInit, Renderer2, OnDestroy, effect } from '@angular/core';

@Directive({
  selector: '[autoFitWidth]',
})
export class AutoFitWidthDirective implements AfterViewInit, OnDestroy {
  private readonly CURSOR_EXTRA_SPACE = 6;
  private readonly MIN_SPACE = 2;
  private mirrorSpan: HTMLElement;
  private cachedFont: string;
  private lastWidth: number = 0;
  private paddingOffset: number = 0;
  private originalValueDescriptor: PropertyDescriptor;
  private inputListener: () => void;
  autoFitWidthMin = input<number>(40);
  autoFitWidthMax = input<number>(400);
  autoFitWidthEnabled = input<boolean>(true);

  constructor(
    private el: ElementRef<HTMLInputElement>,
    private renderer: Renderer2
  ) {
    effect(() => {
      this.autoFitWidthMax();
      if (this.mirrorSpan && this.cachedFont) {
        this.updateWidth();
      }
    });
  }

  ngAfterViewInit() {
    if (!this.autoFitWidthEnabled()) {
      return;
    }
    this.createMirrorSpan();
    setTimeout(() => {
      this.cacheStyles();
      this.setupInputListener();
      this.updateWidth();
    }, 0);
  }

  private setupInputListener() {
    const updateWidth = () => this.updateWidth();

    this.inputListener = updateWidth;
    this.el.nativeElement.addEventListener('input', this.inputListener);

    this.originalValueDescriptor = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');

    const originalSetter = this.originalValueDescriptor.set;

    Object.defineProperty(this.el.nativeElement, 'value', {
      get: this.originalValueDescriptor.get,
      set: function (newValue) {
        originalSetter.call(this, newValue);
        setTimeout(updateWidth, 0);
      },
      configurable: true,
    });
  }

  private removeInputListener() {
    if (this.inputListener) {
      this.el.nativeElement.removeEventListener('input', this.inputListener);
      this.inputListener = null;
    }

    if (this.originalValueDescriptor) {
      Object.defineProperty(this.el.nativeElement, 'value', this.originalValueDescriptor);
      this.originalValueDescriptor = null;
    }
  }

  private createMirrorSpan() {
    this.mirrorSpan = this.renderer.createElement('span');
    this.renderer.setStyle(this.mirrorSpan, 'position', 'absolute');
    this.renderer.setStyle(this.mirrorSpan, 'visibility', 'hidden');
    this.renderer.setStyle(this.mirrorSpan, 'white-space', 'pre');
    this.renderer.setStyle(this.mirrorSpan, 'pointer-events', 'none');
    document.body.appendChild(this.mirrorSpan);
  }

  private cacheStyles() {
    const computedStyle = getComputedStyle(this.el.nativeElement);
    this.cachedFont = computedStyle.font;
    this.renderer.setStyle(this.mirrorSpan, 'font', this.cachedFont);

    const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
    const paddingRight = parseFloat(computedStyle.paddingRight) || 0;
    const borderLeft = parseFloat(computedStyle.borderLeftWidth) || 0;
    const borderRight = parseFloat(computedStyle.borderRightWidth) || 0;
    this.paddingOffset = paddingLeft + paddingRight + borderLeft + borderRight + this.CURSOR_EXTRA_SPACE;
  }

  private updateWidth() {
    if (!this.mirrorSpan) {
      return;
    }
    const value = this.el.nativeElement.value || this.el.nativeElement.placeholder || ' ';
    this.mirrorSpan.textContent = value;

    const measuredWidth = this.mirrorSpan.offsetWidth;
    const newWidth = Math.min(Math.max(measuredWidth, this.autoFitWidthMin()), this.autoFitWidthMax());
    const roundedWidth = Math.round(newWidth) + this.MIN_SPACE;
    if (Math.abs(roundedWidth - this.lastWidth) > this.MIN_SPACE) {
      this.renderer.setStyle(this.el.nativeElement, 'width', `${roundedWidth}px`);
      this.lastWidth = roundedWidth;
    }
  }

  ngOnDestroy() {
    this.removeInputListener();
    if (this.mirrorSpan && this.mirrorSpan.parentNode) {
      this.mirrorSpan.parentNode.removeChild(this.mirrorSpan);
    }
  }
}
